import React, { useEffect, useRef, useState } from 'react';
import { Button, TextField } from '@material-ui/core';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import BrandLogo from './BrandLogo';

import { updateBrandAccent, uploadBrandLogo, removeBrandLogo } from '../api';
import { setBrand } from '../redux/actions/brandActions';
import { setAlert, setSuccess } from '../redux/actions/alertActions';
import { isHex } from '../lib/brandColors';
import { T, FONT_MONO } from '../constants/tokens';

const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp';

/**
 * "School brand" on Platform settings: the accent colour and the logo.
 * Super users only, as the server requires. Each change applies on save and
 * is shown straight away, everywhere, for everyone on their next load.
 *
 * The accent colours buttons, the selected pill, links and focus rings. The
 * alert and status colours stay as the Standard Response Protocol sets them.
 */
function BrandSettings({ accentColor, logoUrl, onBrand, setError, setSuccess }) {
  const [hex, setHex] = useState(accentColor ?? '');
  const [busy, setBusy] = useState(null);
  const fileInput = useRef(null);

  useEffect(() => setHex(accentColor ?? ''), [accentColor]);

  const run = (kind, request, done) => {
    setBusy(kind);
    request()
      .then((brand) => {
        onBrand(brand);
        setSuccess(done);
      })
      .catch((error) => setError(typeof error === 'string' ? error : 'Could not save the school brand.'))
      .finally(() => setBusy(null));
  };

  const valid = hex === '' || isHex(hex);
  const changed = (hex || null) !== (accentColor || null);

  const row = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, padding: 16 };
  const name = { fontSize: 14, fontWeight: 600, lineHeight: '20px', color: T.ink };
  const help = { fontSize: 13, lineHeight: '18px', color: T.body, textWrap: 'pretty' };

  return (
    <section style={{ marginTop: 32 }}>
      <div style={{ fontFamily: FONT_MONO, fontSize: 12, fontWeight: 500, lineHeight: '16px', textTransform: 'uppercase', color: T.body, marginBottom: 12 }}>
        School brand
      </div>
      <div style={{ background: T.surfaceCard, border: '1px solid ' + T.hairline, borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ ...row, borderBottom: '1px solid ' + T.hairlineSoft }}>
          <div style={{ flex: '1 1 260px', minWidth: 0 }}>
            <div style={name}>Accent colour</div>
            <div style={help}>
              Buttons, the selected tab, links and focus rings. Alert colours do not change. Leave empty for the SimulAlert default.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input
              type='color'
              aria-label='Pick the accent colour'
              value={isHex(hex) ? hex : '#23251d'}
              onChange={(e) => setHex(e.target.value)}
              style={{ width: 40, height: 40, padding: 0, border: '1px solid ' + T.hairline, borderRadius: 6, background: 'none', cursor: 'pointer' }}
            />
            <TextField
              value={hex}
              onChange={(e) => setHex(e.target.value.trim())}
              placeholder='#rrggbb'
              variant='outlined'
              size='small'
              error={!valid}
              inputProps={{ 'aria-label': 'Accent colour hex value', style: { fontFamily: FONT_MONO, width: 90 } }}
            />
            <Button
              variant='contained'
              color='primary'
              disabled={!valid || !changed || busy != null}
              onClick={() => run('accent', () => updateBrandAccent(hex || null), hex ? 'Accent colour saved.' : 'Accent colour reset to the SimulAlert default.')}
            >
              {busy === 'accent' ? 'Saving…' : 'Save colour'}
            </Button>
          </div>
        </div>

        <div style={row}>
          <div style={{ flex: '1 1 260px', minWidth: 0 }}>
            <div style={name}>Logo</div>
            <div style={help}>
              Shown in the header and on the sign-in page instead of the SimulAlert logo. PNG, JPEG or WebP, under 1 MB, on a white or transparent background.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ padding: 8, border: '1px solid ' + T.hairlineSoft, borderRadius: 6, background: '#ffffff' }}>
              <BrandLogo />
            </div>
            <input
              ref={fileInput}
              type='file'
              accept={LOGO_ACCEPT}
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) run('logo', () => uploadBrandLogo(file), 'Logo uploaded.');
              }}
            />
            <Button variant='outlined' disabled={busy != null} onClick={() => fileInput.current?.click()}>
              {busy === 'logo' ? 'Uploading…' : (logoUrl ? 'Replace logo' : 'Upload logo')}
            </Button>
            {logoUrl &&
              <Button variant='outlined' disabled={busy != null} onClick={() => run('remove', removeBrandLogo, 'Logo removed. The SimulAlert logo is back.')}>
                {busy === 'remove' ? 'Removing…' : 'Remove'}
              </Button>}
          </div>
        </div>
      </div>
    </section>
  );
}

BrandSettings.propTypes = {
  accentColor: PropTypes.string,
  logoUrl: PropTypes.string,
  onBrand: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  setSuccess: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  accentColor: state.brand?.accentColor ?? null,
  logoUrl: state.brand?.logoUrl ?? null
});

const mapDispatchToProps = (dispatch) => ({
  onBrand: (brand) => dispatch(setBrand(brand)),
  setError: (msg) => setAlert(dispatch, msg),
  setSuccess: (msg) => setSuccess(dispatch, msg)
});

export default connect(mapStateToProps, mapDispatchToProps)(BrandSettings);
